import { useEffect, useMemo, useState } from "react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.verixxon.com";

const formatMoney = (value) => `₵${Number(value || 0).toFixed(2)}`;

const preparePackageRow = (item) => ({
  ...item,
  sellingPriceInput: String(Number(item?.sellingPrice || 0).toFixed(2)),
});

const getTransactions = (overview) => {
  const rows = overview?.transactions?.transactions || overview?.transactions?.data?.transactions;
  return Array.isArray(rows) ? rows : [];
};

const MtnDataBundlesPage = () => {
  const [packages, setPackages] = useState([]);
  const [providerOverview, setProviderOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [savingCode, setSavingCode] = useState("");

  const loadPackages = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/mtn-gh-data-bundles`);
      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload.message || "Failed to load MTN bundle catalog.");
      }

      const rows = Array.isArray(payload.data) ? payload.data.map(preparePackageRow) : [];
      setPackages(rows);
      setProviderOverview(payload.provider || null);
    } catch (loadError) {
      setError(loadError.message || "Unable to connect to backend service.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPackages();
  }, []);

  const totals = useMemo(() => {
    const enabledCount = packages.filter((item) => item.enabled).length;
    const lowestSellingPrice = packages.length > 0
      ? Math.min(...packages.map((item) => Number(item.sellingPriceInput || item.sellingPrice || 0)))
      : 0;

    return {
      total: packages.length,
      enabled: enabledCount,
      lowestSellingPrice,
      providerBalance: Number(providerOverview?.balance?.balance || 0),
      requestCount: Number(
        providerOverview?.usageStats?.totalCalls
          || providerOverview?.usageStats?.requestsToday
          || providerOverview?.usageStats?.todayCalls
          || 0
      ),
    };
  }, [packages, providerOverview]);

  const providerTransactions = useMemo(() => getTransactions(providerOverview).slice(0, 4), [providerOverview]);

  const handleFieldChange = (packageCode, field, value) => {
    setPackages((prev) =>
      prev.map((item) => (item.packageCode === packageCode ? { ...item, [field]: value } : item))
    );
  };

  const savePackage = async (item) => {
    setSavingCode(item.packageCode);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/mtn-gh-data-bundles/${item.packageCode}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          sellingPrice: Number(item.sellingPriceInput),
          enabled: Boolean(item.enabled),
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.message || "Failed to update MTN package.");
      }

      setPackages((prev) =>
        prev.map((pkg) => (pkg.packageCode === item.packageCode ? preparePackageRow(payload.data) : pkg))
      );
      setSuccess(`${item.bundleLabel} updated successfully.`);
    } catch (saveError) {
      setError(saveError.message || "Unable to save MTN package changes.");
    } finally {
      setSavingCode("");
    }
  };


  return (
    <section className="admin-page-card mtn-admin-page">
      <div className="mtn-admin-hero">
        <div className="mtn-admin-hero-top">
          <div className="mtn-admin-hero-copy">
            <p className="hero-kicker">MTN GH Bundles</p>
            <h2>MTN bundle control center</h2>
            <p>
              Manage DataMart connectivity, provider visibility, and selling prices for every MTN Ghana
              package from one clean dashboard.
            </p>

            <div className="mtn-admin-hero-badges">
              <span className="mtn-admin-badge">MTN Ghana</span>
              <span className="mtn-admin-badge">
                {providerOverview?.configured ? "API key ready" : "API key needed"}
              </span>
            </div>
          </div>

          <div className="mtn-admin-hero-actions">
            <button type="button" className="inventory-tab inventory-tab-active" onClick={loadPackages}>
              Refresh Catalog
            </button>
          </div>
        </div>

        <div className="mtn-admin-provider-inline">
          <div className="mtn-admin-panel-head mtn-admin-panel-head-inline">
            <div>
              <p className="hero-kicker">Provider health</p>
              <h3>DataMart integration overview</h3>
            </div>
          </div>

          <div className="mtn-admin-provider-grid">
            <article className="dashboard-card mtn-admin-provider-card">
              <span>API status</span>
              <strong>{providerOverview?.configured ? (providerOverview?.connected ? "Connected" : "Key set") : "Not set"}</strong>
              <small>Network code: {providerOverview?.networkCode || "YELLO"}</small>
            </article>

            <article className="dashboard-card mtn-admin-provider-card">
              <span>Usage today</span>
              <strong>{totals.requestCount}</strong>
              <small>Recent API calls tracked by DataMart.</small>
            </article>

            <article className="dashboard-card mtn-admin-provider-card">
              <span>Provider balance</span>
              <strong>{providerOverview?.configured ? formatMoney(totals.providerBalance) : "Not set"}</strong>
              <small>Wallet available for current delivery requests.</small>
            </article>
          </div>

          {providerOverview?.message ? <p className="mtn-admin-muted">{providerOverview.message}</p> : null}
          {Array.isArray(providerOverview?.errors) && providerOverview.errors.length > 0 ? (
            <p className="mtn-admin-muted">Provider notes: {providerOverview.errors.join(" • ")}</p>
          ) : null}
        </div>
      </div>

      {error ? <p className="form-error">{error}</p> : null}
      {success ? <p className="form-success">{success}</p> : null}

      <div className="mtn-admin-summary-grid">
        <article className="dashboard-card mtn-admin-summary-card">
          <span>Total bundles</span>
          <strong>{totals.total}</strong>
          <small>Catalog rows currently synced.</small>
        </article>
        <article className="dashboard-card mtn-admin-summary-card">
          <span>Enabled for users</span>
          <strong>{totals.enabled}</strong>
          <small>Visible in the user app right now.</small>
        </article>
        <article className="dashboard-card mtn-admin-summary-card">
          <span>Lowest selling price</span>
          <strong>{totals.total ? formatMoney(totals.lowestSellingPrice) : "—"}</strong>
          <small>Cheapest live MTN offer.</small>
        </article>
        <article className="dashboard-card mtn-admin-summary-card">
          <span>DataMart balance</span>
          <strong>{providerOverview?.configured ? formatMoney(totals.providerBalance) : "Not set"}</strong>
          <small>Provider wallet available for delivery.</small>
        </article>
      </div>

      <div className="mtn-admin-insight-grid">
        <section className="mtn-admin-remote-transactions">
          <div className="mtn-admin-panel-head">
            <div>
              <p className="hero-kicker">Live activity</p>
              <h3>Recent provider transactions</h3>
            </div>
          </div>

          {providerTransactions.length > 0 ? (
            <div className="mtn-admin-remote-scroll-wrap">
              <div className="mtn-admin-remote-transaction-list">
                {providerTransactions.map((item, index) => (
                  <article key={`${item.reference || index}`} className="mtn-admin-remote-transaction">
                    <strong>{item.reference || "Data purchase"}</strong>
                    <span>{item.status || "completed"}</span>
                    <span>{item.gateway || item.type || "wallet"}</span>
                    <span>{formatMoney(item.amount || 0)}</span>
                  </article>
                ))}
              </div>
            </div>
          ) : (
            <p className="mtn-admin-muted">Recent provider transactions will appear here after the next live order.</p>
          )}
        </section>
      </div>

      <section className="mtn-admin-package-section">
        <div className="mtn-admin-panel-head">
          <div>
            <p className="hero-kicker">Bundle pricing</p>
            <h3>Customize each MTN bundle</h3>
          </div>
        </div>

        {isLoading ? (
          <p className="provider-empty-row">Loading MTN data bundles...</p>
        ) : packages.length === 0 ? (
          <div className="mtn-admin-empty-state">
            <h4>No MTN data bundles found.</h4>
            <p>Refresh the catalog after connecting the DataMart provider.</p>
          </div>
        ) : (
          <div className="mtn-admin-package-grid">
            {packages.map((item) => (
              <article key={item.packageCode} className="mtn-admin-package-card">
                <div className="mtn-admin-package-top">
                  <div className="mtn-admin-bundle-label">
                    <strong>{item.bundleLabel}</strong>
                    <span>{item.durationLabel || "No Expiry"}</span>
                  </div>
                  <span className={item.enabled ? "mtn-admin-package-chip" : "mtn-admin-package-chip mtn-admin-package-chip-off"}>
                    {item.enabled ? "Enabled" : "Hidden"}
                  </span>
                </div>

                <div className="mtn-admin-package-prices">
                  <div>
                    <span>Source price</span>
                    <strong>{formatMoney(item.sourcePrice)}</strong>
                  </div>
                  <div>
                    <span>Your current price</span>
                    <strong>{formatMoney(item.sellingPriceInput || item.sellingPrice)}</strong>
                  </div>
                </div>

                <label className="mtn-admin-field">
                  <span>Selling price</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="mtn-admin-price-input"
                    value={item.sellingPriceInput}
                    onChange={(event) => handleFieldChange(item.packageCode, "sellingPriceInput", event.target.value)}
                  />
                </label>

                <label className="mtn-admin-toggle mtn-admin-toggle-card">
                  <input
                    type="checkbox"
                    checked={Boolean(item.enabled)}
                    onChange={(event) => handleFieldChange(item.packageCode, "enabled", event.target.checked)}
                  />
                  <span>{item.enabled ? "Show this bundle in user app" : "Keep this bundle hidden"}</span>
                </label>

                <div className="mtn-admin-card-actions">
                  <button
                    type="button"
                    className="provider-action-btn"
                    onClick={() => savePackage(item)}
                    disabled={savingCode === item.packageCode}
                  >
                    {savingCode === item.packageCode ? "Saving..." : "Save changes"}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </section>
  );
};

export default MtnDataBundlesPage;
